import { Component } from '@theme/component';

class TglHeroCycle extends Component {
  #timer;
  #observer;
  #preloader;
  #abort;
  #motion = matchMedia('(prefers-reduced-motion: reduce)');
  #visible = false;
  #paused = false;
  #index = 0;
  #generation = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    const button = this.parentElement.querySelector('[data-cycle-toggle]');
    if (button) {
      button.hidden = this.querySelectorAll('picture').length < 2 || this.#motion.matches;
      button.addEventListener('click', () => {
        this.#paused = !this.#paused;
        button.setAttribute('aria-pressed', String(this.#paused));
        button.setAttribute('aria-label', this.#paused ? button.dataset.playLabel : button.dataset.pauseLabel);
        button.querySelector('span').textContent = this.#paused ? '▶' : 'Ⅱ';
        this.#schedule();
      }, options);
    }
    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      this.#schedule();
    }, { threshold: 0.15 });
    this.#observer.observe(this);
    this.#preloader = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      const first = this.querySelector('picture');
      if (first) this.#loadFrame(first);
      this.#preloader.disconnect();
    }, { rootMargin: '300px 0px' });
    this.#preloader.observe(this);
    document.addEventListener('visibilitychange', () => this.#schedule(), options);
    this.#motion.addEventListener('change', () => {
      if (button) button.hidden = this.#motion.matches;
      this.#schedule();
    }, options);
  }

  #schedule() {
    clearTimeout(this.#timer);
    this.#generation++;
    if (!this.#visible || this.#paused || this.#motion.matches || document.hidden) return;
    this.#timer = setTimeout(() => this.#advance(), 4800);
  }

  #loadFrame(frame) {
    const template = frame.querySelector('template');
    if (template) {
      frame.append(template.content.cloneNode(true));
      template.remove();
    }
    const image = frame.querySelector('img');
    image.loading = 'eager';
    return image;
  }

  async #advance() {
    const generation = this.#generation;
    const frames = [...this.querySelectorAll('picture')];
    if (frames.length < 2) return;
    const next = (this.#index + 1) % frames.length;
    const image = this.#loadFrame(frames[next]);
    try { await image.decode(); } catch { this.#schedule(); return; }
    if (!this.isConnected || generation !== this.#generation) return;
    frames[this.#index].classList.remove('is-active');
    frames[next].classList.add('is-active');
    this.#index = next;
    this.#schedule();
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#generation++;
    this.#observer?.disconnect();
    this.#preloader?.disconnect();
    this.#abort?.abort();
    super.disconnectedCallback();
  }
}

if (!customElements.get('tgl-hero-cycle')) customElements.define('tgl-hero-cycle', TglHeroCycle);
